/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import { OnChangeEventContext } from 'react-crane'
import ErrorIcon from '@mui/icons-material/Error'

import {
  BoardGroupFragment,
  BoardItemFragment,
  PlanningTerm,
  TermOption
} from '../../../../../generated/graphql'
import { TermSelect, TermOptionsSelect } from '..'
import { subtitle1Css, tertiaryBodyCss } from '../../../../../lib/styles/typography'
import { alternateGray, errorIconColor } from '../../../../../lib/styles/colors'
import { Menu } from '../../../../shared/menu'
import { formatCredits } from '../../../../../lib/utils'
import { DangerBanner } from '../../../../shared/alerts'
import { useReadOnly } from '../../../../../lib/hooks/useReadOnly'

export type PlanGroupTitleProps = {
  /** Number of total alerts cross items for group */
  alertCount?: number
  /** Label to use for credits */
  creditsLabel: string
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Event callback when Add Comment is clicked */
  onAddComment: () => void
  /** Event callback when Delete Term is clicked */
  onDelete: () => void
  /** Event callback when Move Term is clicked */
  onMove: () => void
  /** Event callback when term value is changed on a plan */
  onTermChange: (e: OnChangeEventContext) => void
  /** Event callback when term option is changed */
  onTermOptionChange: (e: OnChangeEventContext) => void
  /** Indicates if this plan/template is read-only */
  readOnly?: boolean
  /** Whether the add comment field is visible */
  showAddComment: boolean
  /** Label to use for term */
  termLabel: string
  /** Optional list of configured term options to use for template term naming */
  termOptions?: Array<TermOption>
  /** List of terms to display in the term select */
  terms?: Array<PlanningTerm>
}

const defaultProps: Pick<PlanGroupTitleProps, 'alertCount' | 'readOnly' | 'termOptions' | 'terms'> =
  {
    alertCount: undefined,
    readOnly: false,
    termOptions: undefined,
    terms: undefined
  }

const readOnlyTitleCss = css`
  ${subtitle1Css}
  overflow: hidden;
  padding-top: 14px;
  padding-left: 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const menuCss = css`
  justify-self: end;
  grid-row: 1/3;
  grid-column: 3;

  /* override PlanGroupLane css */
  button {
    background-color: transparent;
  }
`
const creditsContainerCss = css`
  grid-column: 1 / 3;
`
const groupCreditsCss = css`
  ${tertiaryBodyCss}
  color: ${alternateGray};
  padding-left: 6px;
`
const alertCss = css`
  grid-column: 2;
  align-items: center;
  padding: 2px;
  min-height: 32px;
  height: 32px;
  margin-top: 6px;

  .alertIcon {
    padding-right: 5px;
    margin-top: 0;

    svg {
      font-size: 18px;
    }
  }
`
const bufferCss = css`
  height: 10px;
`

const PlanGroupTitle = ({
  alertCount,
  creditsLabel,
  group,
  isTemplate,
  onAddComment,
  onDelete,
  onMove,
  onTermChange,
  onTermOptionChange,
  readOnly,
  showAddComment,
  termLabel,
  termOptions,
  terms
}: PlanGroupTitleProps) => {
  const { isReadOnly } = useReadOnly()
  const [isTitleFocused, setIsTitleFocused] = useState(false)

  const handleTitleFocus = () => setIsTitleFocused(true)
  const handleTitleBlur = () => setIsTitleFocused(false)

  const { externalTermId, id, items, termSequence, title } = group
  const menuItems = [
    {
      ariaLabel: showAddComment ? 'The add comment field is already displayed' : 'Add a comment',
      text: 'Add Comment',
      onClick: onAddComment
    },
    {
      text: `Move ${termLabel}`,
      onClick: onMove
    },
    {
      text: `Delete ${termLabel}`,
      onClick: onDelete
    }
  ]

  const menuReadOnly = [
    {
      ariaLabel: showAddComment ? 'The add comment field is already displayed' : 'Add a comment',
      text: 'Add Comment',
      onClick: onAddComment
    }
  ]

  const getFormattedCredits = () => {
    if (!items) {
      return 0
    }

    const creditsMin = items.reduce((creditAccumulator: number, item: BoardItemFragment) => {
      // If the item is a non-course or a placeholder without a selected course, use non course credits for the calc
      if (!item?.subjectId && item.nonCourseCreditsMin && item.nonCourseCreditsMin > 0) {
        return creditAccumulator + item.nonCourseCreditsMin
      }
      if (item?.subjectId && item.creditsMin && item.creditsMin > 0) {
        return creditAccumulator + item.creditsMin
      }
      return creditAccumulator
    }, 0)

    const creditsMax = items.reduce((creditAccumulator: number, item: BoardItemFragment) => {
      // If the item is a non-course or a placeholder without a selected course, use non course credits for the calc
      if (!item?.subjectId && item.nonCourseCreditsMax && item.nonCourseCreditsMax > 0) {
        return creditAccumulator + item.nonCourseCreditsMax
      }
      if (item?.subjectId && item.creditsMax && item.creditsMax > 0) {
        return creditAccumulator + item.creditsMax
      }
      return creditAccumulator
    }, 0)

    const formattedCredits = formatCredits(creditsMin, creditsMax)

    return formattedCredits || 0
  }

  const groupAlertCount = alertCount || 0
  const containerCss = css`
    padding-left: 8px;
    display: grid;
    grid-template-columns: ${!isTitleFocused && groupAlertCount
      ? 'minmax(0, 1fr) 115px 35px'
      : 'auto auto 35px'};
    grid-row: 1;
    grid-gap: 3px;
  `
  const titleCss = css`
    grid-column: ${!isTitleFocused && groupAlertCount ? '1' : '1 / 3'};
    display: flex;
  `

  const renderTitle = () => {
    if (isTemplate && !readOnly && termOptions?.length) {
      return (
        <TermOptionsSelect
          id={group.id}
          onChange={onTermOptionChange}
          termOptions={termOptions}
          termSequence={termSequence || 0}
          title={title}
        />
      )
    }

    // In case the selected term no longer exists in the list of terms,
    // OR this is a read-only template, OR there is no term type config on templates
    // display the title as read-only
    const selectedTerm = terms?.find((t) => t.termCode === externalTermId)
    if (
      (externalTermId && !selectedTerm) ||
      (isTemplate && readOnly) ||
      (isTemplate && !termOptions?.length) ||
      isReadOnly
    ) {
      const formattedSequence = termSequence || ''
      return <span css={readOnlyTitleCss}>{`${title} ${isTemplate ? formattedSequence : ''}`}</span>
    }

    return (
      <TermSelect
        group={group}
        isFocused={isTitleFocused}
        onBlur={handleTitleBlur}
        onFocus={handleTitleFocus}
        onTermChange={onTermChange}
        termLabel={termLabel}
        terms={terms || []}
      />
    )
  }

  return (
    <div css={containerCss}>
      <span css={titleCss}>{renderTitle()}</span>
      {!isTitleFocused && !!groupAlertCount && (
        <DangerBanner
          css={alertCss}
          icon={<ErrorIcon />}
          iconClassName="alertIcon"
          iconColor={errorIconColor}
        >
          <span css={tertiaryBodyCss}>
            {alertCount} ALERT{groupAlertCount > 1 && 'S'}
          </span>
        </DangerBanner>
      )}
      <div css={menuCss}>
        {!readOnly && (
          <Menu id={`group-actions-${id}`} menuItems={menuItems} ariaLabel={`${title} Options`} />
        )}
        {readOnly && isReadOnly && (
          <Menu
            id={`group-actions-${id}`}
            menuItems={menuReadOnly}
            ariaLabel={`${title} Options`}
          />
        )}
      </div>
      {!isTitleFocused && (
        <div css={creditsContainerCss}>
          <span css={groupCreditsCss}>
            {getFormattedCredits()} {creditsLabel}
          </span>
        </div>
      )}
      {isTitleFocused && <div css={bufferCss} />}
    </div>
  )
}

PlanGroupTitle.defaultProps = defaultProps

export default PlanGroupTitle
